import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';
import FaqTips from 'timeOffReports/components/FaqTips.component';
import TableTippingRates from 'timeOffReports/components/TableTippingRates.component';

import IMG_LOGO from 'assets/slickpay-splash/logoes/Slickpay.png';
import IMG_COMPLIANT from 'assets/slickpay-splash/tip/compliant-card.png';
import ICO_ERN from 'assets/slickpay-splash/tip/ern.png';
import ICO_LEGALL from 'assets/slickpay-splash/tip/legal.png';
import ICO_SIMPLE from 'assets/slickpay-splash/tip/simple.png';
import ICO_TAX from 'assets/slickpay-splash/tip/tax.png';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import CurrencySign from 'components/price/CurrencySign.component';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import windowScrollToPosition from 'utils/dom/windowScrollTo.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import C from 'utils/values/constant.util';

import CN from './TipsHrLayout.module.scss';

const getCardData = (t) => [
  {
    id: 'earn',
    src: ICO_ERN,
    title: t('staffKpisReports.staffTipping.cards.earn.title'),
    text: [
      t('staffKpisReports.staffTipping.cards.earn.description1'),
      t('staffKpisReports.staffTipping.cards.earn.description2'),
    ].join(' '),
  },
  {
    id: 'simple',
    src: ICO_SIMPLE,
    title: t('staffKpisReports.staffTipping.cards.simple.title'),
    text: [
      t('staffKpisReports.staffTipping.cards.simple.description1'),
      t('staffKpisReports.staffTipping.cards.simple.description2'),
    ].join(' '),
  },
  {
    id: 'tax',
    src: ICO_TAX,
    title: t('staffKpisReports.staffTipping.cards.tax.title'),
    text: t('staffKpisReports.staffTipping.cards.tax.description'),
  },
  {
    id: 'legal',
    src: ICO_LEGALL,
    title: t('staffKpisReports.staffTipping.cards.legal.title'),
    text: [
      t('staffKpisReports.staffTipping.cards.legal.description1'),
      t('staffKpisReports.staffTipping.cards.legal.description2'),
    ].join(' '),
  },
];

const getBullets = (t) =>
  C([
    t('staffKpisReports.staffTipping.compliantSection.requirementBullets.tips'),
    t('staffKpisReports.staffTipping.compliantSection.requirementBullets.taxes'),
    t('staffKpisReports.staffTipping.compliantSection.requirementBullets.payOut'),
    t('staffKpisReports.staffTipping.compliantSection.requirementBullets.policy'),
    t('staffKpisReports.staffTipping.compliantSection.requirementBullets.records'),
  ]);

const TipsHrLayout = () => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();

  useEffect(() => void windowScrollToPosition(0, 0), []);

  const CARD_DATA = getCardData(t);
  const DATA_BULLETS = getBullets(t);

  return (
    <div
      data-bem="TipsHrLayout"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="TipsHrLayout__wrap" className={CN.wrap}>
        <img src={IMG_LOGO} alt="Slick logo" className={CN.logo} />
        <div className={CN.pageTitle}>
          {t('staffKpisReports.staffTipping.title')} {S.creditCard}
        </div>

        <div data-bem="TipsHrLayout__group" className={CN.cardGroup}>
          <div data-bem="TipsHrLayout__card" className={CN.card}>
            <div className={CN.badgeContainer}>
              <Badge name={t('staffKpisReports.staffTipping.tipsBadge')} variant="fine" />
            </div>
            <div className={CN.cardTitle}>{t('staffKpisReports.staffTipping.tipTitle')}</div>
            <div>{t('staffKpisReports.staffTipping.tipDescription')}</div>
          </div>
          <div className={CN.imgContainer}>
            <div data-bem="TipsHrLayout__gif-image" className={CN.gifContainer}></div>
          </div>
        </div>
        <div className={CN.tipsTitle}>
          {t('staffKpisReports.staffTipping.whyPart1')}
          <span className={CN.markedTipsTitle}>{t('staffKpisReports.staffTipping.whyPart2')}</span>
        </div>
        <div data-bem="TipsHrLayout__group" className={CN.cardGroup}>
          {CARD_DATA.map((card) => (
            <div key={card.id} data-bem="TipsHrLayout__card" className={CN.card}>
              <img className={CN.cardIcon} src={card.src} alt={card.title} />
              <div className={CN.cardTitle}>{card.title}</div>
              <div>{card.text}</div>
              {card.id === 'tax' && (
                <div className={CN.linkContainer} data-bem="TipsHrLayout__link">
                  <NavLink to={linkTo({ pattern: LINK.salonOnline, params: { salonId } })}>
                    <Button variant="primaryForce" onClick={alwaysNoop}>
                      <span className={CN.link}>{t('staffKpisReports.staffTipping.setupAccountButton')}</span>
                    </Button>
                  </NavLink>
                </div>
              )}
            </div>
          ))}
        </div>

        <TableTippingRates />
      </div>

      <div data-bem="TipsHrLayout__compliant" className={CN.sectionCompliant}>
        <div className={CN.wrap}>
          <div className={CN.subTitle}>
            {t('staffKpisReports.staffTipping.compliantSection.titlePart1')}
            <span className={CN.markedCompliantTitle}>
              {t('staffKpisReports.staffTipping.compliantSection.titlePart2')}
            </span>
          </div>
          <div className={CN.description}>
            <span>{t('staffKpisReports.staffTipping.compliantSection.subtitle1')}</span>
            <span>{t('staffKpisReports.staffTipping.compliantSection.subtitle2')}</span>
          </div>
          <div
            className={cn({
              [CN.cardGroup]: true,
              [CN.reverseOrder]: true,
            })}
          >
            <div
              className={cn({
                [CN.card]: true,
                [CN.center]: true,
              })}
            >
              <img className={CN.compliantImg} src={IMG_COMPLIANT} alt="Is your business compliant illustration" />
            </div>
            <div
              className={cn({
                [CN.card]: true,
                [CN.text]: true,
              })}
            >
              <div className={CN.cardTitle}>{t('staffKpisReports.staffTipping.compliantSection.requirementTitle')}</div>
              <Bullets data={DATA_BULLETS} dot variantInvert />
            </div>
          </div>

          <div className={CN.description}>
            {t('staffKpisReports.staffTipping.compliantSection.footer')} <CurrencySign value="5,000" />.
          </div>
        </div>
      </div>
      <div className={CN.wrap}>
        <FaqTips />
      </div>
    </div>
  );
};

export default TipsHrLayout;
